import React, { Suspense, lazy, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, CircularProgress, Dialog, IconButton, Stack, Tooltip, Typography } from '@material-ui/core'
import Fullscreen from '@material-ui/icons/Fullscreen'
import Close from '@material-ui/icons/Close'

// Lazy so the igv.js chunk stays out of the dashboard / CNV-table initial
// bundle — it loads only when a gene browser is actually opened.
const GeneIgv = lazy(() => import('./GeneIgv'))

const Fallback = () => (
  <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 300, width: '100%' }}>
    <CircularProgress />
  </Stack>
)

/**
 * Presentational shell around GeneIgv: a caller-supplied gene selector, the
 * browser itself, and a fullscreen button that reopens the browser in a
 * fullscreen dialog with the selector on the left (so the gene can be switched
 * without leaving fullscreen).
 *
 * Gene resolution lives in the caller (search box for the dashboard tab,
 * overlapping-gene list for the CNV table); this component only lays things out.
 */
const GeneIgvViewer = function ({
  region,
  geneSymbol,
  fileId,
  analysisWrapperId,
  svSource,
  extraFeatures,
  selector,
  caption,
  emptyHint,
  title,
  inlineHeight,
}) {
  const [fullscreen, setFullscreen] = useState(false)

  // Remount igv.js on gene / fullscreen change so it initialises at the right
  // size and re-anchors cleanly (simpler than resizing a live browser).
  const igvKey = `${geneSymbol || 'none'}:${region?.start ?? ''}-${region?.end ?? ''}:${fullscreen ? 'fs' : 'inline'}`

  const renderIgv = (height) =>
    region ? (
      <Suspense fallback={<Fallback />}>
        <GeneIgv
          key={igvKey}
          region={region}
          geneSymbol={geneSymbol}
          fileId={fileId}
          analysisWrapperId={analysisWrapperId}
          svSource={svSource}
          extraFeatures={extraFeatures}
          height={height}
        />
      </Suspense>
    ) : (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 200, width: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyHint}
        </Typography>
      </Stack>
    )

  const captionNode = caption ? (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
      {caption}
    </Typography>
  ) : null

  return (
    <Box sx={{ width: '100%' }}>
      {/* Inline layout: selector + fullscreen button, browser below. Hidden
          while the fullscreen dialog is open so only one igv.js browser lives. */}
      {!fullscreen && (
        <>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ flexGrow: 1 }}>{selector}</Box>
            <Tooltip title="Fullscreen" arrow>
              <IconButton size="small" onClick={() => setFullscreen(true)}>
                <Fullscreen />
              </IconButton>
            </Tooltip>
          </Stack>
          {captionNode}
          {renderIgv(inlineHeight)}
        </>
      )}

      <Dialog fullScreen open={fullscreen} onClose={() => setFullscreen(false)}>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Tooltip title="Exit fullscreen" arrow>
            <IconButton onClick={() => setFullscreen(false)}>
              <Close />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" sx={{ height: 'calc(100vh - 57px)' }}>
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              p: 2,
              borderRight: '1px solid',
              borderColor: 'divider',
              overflowY: 'auto',
            }}
          >
            {fullscreen && selector}
            {captionNode}
          </Box>
          <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>{fullscreen && renderIgv(Math.max(inlineHeight, 700))}</Box>
        </Stack>
      </Dialog>
    </Box>
  )
}

GeneIgvViewer.propTypes = {
  region: PropTypes.shape({
    chrom: PropTypes.string,
    start: PropTypes.number,
    end: PropTypes.number,
  }),
  geneSymbol: PropTypes.string,
  fileId: PropTypes.string,
  analysisWrapperId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  svSource: PropTypes.oneOf(['both', 'cnv', 'sv']),
  extraFeatures: PropTypes.array,
  selector: PropTypes.node,
  caption: PropTypes.node,
  emptyHint: PropTypes.node,
  title: PropTypes.string,
  inlineHeight: PropTypes.number,
}

GeneIgvViewer.defaultProps = {
  region: null,
  geneSymbol: null,
  fileId: null,
  analysisWrapperId: null,
  svSource: 'both',
  extraFeatures: null,
  selector: null,
  caption: null,
  emptyHint: 'Select a gene to open the browser.',
  title: 'Gene browser',
  inlineHeight: 600,
}

export default GeneIgvViewer
